import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import { useContractConfidenceScore, type ConfidenceGrade, type DimensionResult } from '../hooks/useContractConfidenceScore';
import { useIFRiskHistorico } from '../hooks/useIFRiskHistorico';
import { MiniSparkline } from './MiniSparkline';

interface Props {
  contratoId: string;
}

const GRADE_CONFIG: Record<ConfidenceGrade, { color: string; bgColor: string; icon: React.ElementType; label: string }> = {
  A: { color: 'text-success', bgColor: 'bg-success/10', icon: ShieldCheck, label: 'Confiável' },
  B: { color: 'text-warning', bgColor: 'bg-warning/10', icon: ShieldCheck, label: 'Atenção' },
  C: { color: 'text-orange-500', bgColor: 'bg-orange-500/10', icon: ShieldAlert, label: 'Cautela' },
  D: { color: 'text-destructive', bgColor: 'bg-destructive/10', icon: ShieldAlert, label: 'Risco Elevado' },
};

const GRADE_BAR: Record<ConfidenceGrade, string> = {
  A: 'bg-success',
  B: 'bg-warning',
  C: 'bg-orange-500',
  D: 'bg-destructive',
};

/** Grouped by affinity: left column = financial/contractual, right column = operational/evidence */
const DIMENSION_COL_LEFT: (keyof ReturnType<typeof useContractConfidenceScore>['dimensions'])[] = [
  'integridade',
  'coerencia',
  'aderencia',
];
const DIMENSION_COL_RIGHT: (keyof ReturnType<typeof useContractConfidenceScore>['dimensions'])[] = [
  'medicoes',
  'evidencias',
  'criptografia',
];

function dimensionBarColor(raw: number): string {
  if (raw >= 70) return 'bg-success';
  if (raw >= 40) return 'bg-warning';
  return 'bg-destructive';
}

function DimensionBar({ dim, dimKey }: { dim: DimensionResult; dimKey: string }) {
  const barColor = dimensionBarColor(dim.raw);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-foreground leading-tight truncate">
          {dim.label}
        </span>
        <span className="text-xs font-mono tabular-nums text-muted-foreground shrink-0">
          {dim.raw}<span className="text-muted-foreground/40">/100</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${dim.raw}%` }}
        />
      </div>
      <p className="text-[10px] leading-snug text-muted-foreground/70 line-clamp-1">
        {dim.explanation}
      </p>
    </div>
  );
}

export const IFConfidenceScore: React.FC<Props> = ({ contratoId }) => {
  const data = useContractConfidenceScore(contratoId);

  if (data.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-16 w-32" />
        <Skeleton className="h-2 w-full" />
        <div className="grid grid-cols-2 gap-6">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      </div>
    );
  }

  const { maturity } = data;
  const isImmature = maturity === 'immature';
  const gradeCfg = GRADE_CONFIG[data.grade];
  const GradeIcon = gradeCfg.icon;

  return (
    <div>
      {/* ── Score Header ─────────────────────────────────────── */}
      <div className="mb-6">
        <p className="text-[11px] font-semibold text-muted-foreground tracking-widest uppercase mb-3">
          Índice de Confiança
        </p>
        {isImmature ? (
          <div className="flex items-center gap-2.5">
            <ShieldQuestion className="h-6 w-6 text-muted-foreground/40" />
            <span className="text-sm text-muted-foreground">{data.maturityLabel}</span>
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-3">
              <span className={`text-5xl font-extrabold tabular-nums tracking-tighter font-display ${gradeCfg.color}`}>
                {data.score}
              </span>
              <span className="text-base text-muted-foreground/40 font-medium">/100</span>
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${gradeCfg.bgColor} ml-auto`}>
                <GradeIcon className={`h-3.5 w-3.5 ${gradeCfg.color}`} />
                <span className={`text-xs font-bold ${gradeCfg.color}`}>{gradeCfg.label}</span>
              </div>
            </div>
            {/* Zoned progress bar with A/B/C/D markers */}
            <div className="relative mt-4">
              <div className="h-2.5 rounded-full overflow-hidden flex">
                <div className="bg-destructive/20 h-full" style={{ width: '39%' }} />
                <div className="bg-orange-500/20 h-full" style={{ width: '25%' }} />
                <div className="bg-warning/20 h-full" style={{ width: '20%' }} />
                <div className="bg-success/20 h-full" style={{ width: '16%' }} />
              </div>
              {/* Score indicator */}
              <div
                className={`absolute top-0 left-0 h-2.5 rounded-full transition-all duration-700 ${GRADE_BAR[data.grade]}`}
                style={{ width: `${data.score}%`, opacity: 0.9 }}
              />
              {/* Zone labels */}
              <div className="flex mt-1">
                <span className="text-[9px] font-mono text-destructive/60" style={{ width: '39%' }}>D</span>
                <span className="text-[9px] font-mono text-orange-500/60" style={{ width: '25%' }}>C</span>
                <span className="text-[9px] font-mono text-warning/60" style={{ width: '20%' }}>B</span>
                <span className="text-[9px] font-mono text-success/60 text-right" style={{ width: '16%' }}>A</span>
              </div>
            </div>
          </>
        )}
        {maturity === 'preliminary' && (
          <p className="text-[11px] text-muted-foreground/60 mt-2 italic">{data.maturityLabel}</p>
        )}
      </div>

      {/* ── Sparkline — historical score evolution ────────── */}
      {!isImmature && <ScoreSparkline contratoId={contratoId} />}

      {/* ── Dimension Breakdown — 2 columns by affinity ────── */}
      {!isImmature && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 pt-5 border-t border-border/30">
          {/* Left column: Financial / Contractual */}
          <div className="space-y-4">
            <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider">Financeiro & Contratual</p>
            {DIMENSION_COL_LEFT.map((key) => (
              <DimensionBar key={key} dimKey={key} dim={data.dimensions[key]} />
            ))}
          </div>
          {/* Right column: Operational / Evidence */}
          <div className="space-y-4">
            <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider">Operacional & Evidências</p>
            {DIMENSION_COL_RIGHT.map((key) => (
              <DimensionBar key={key} dimKey={key} dim={data.dimensions[key]} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Score Sparkline sub-component ─────────────────────────────── */
function ScoreSparkline({ contratoId }: { contratoId: string }) {
  const { data: history = [] } = useIFRiskHistorico(contratoId, 180);
  
  if (history.length < 3) return null;

  // Use score_composto as proxy (inverted: lower risk = higher confidence)
  const scores = history
    .sort((a, b) => (a.data_referencia || '').localeCompare(b.data_referencia || ''))
    .map(h => Math.max(0, 100 - (Number(h.score_composto) || 0)));

  return (
    <div className="flex items-center gap-3 mt-4 mb-2">
      <p className="text-[10px] font-medium text-muted-foreground/50 uppercase tracking-wider shrink-0">
        Evolução 6m
      </p>
      <MiniSparkline data={scores} width={120} height={28} />
    </div>
  );
}
